import {useEffect, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {quoteCart} from "../api/cart";

// Coalesce simultaneous readers without caching a price across later edits.
const pendingQuotes = new Map();
const retryReaders = new Set();
const readQuote = key => {
  if (pendingQuotes.has(key)) return pendingQuotes.get(key);
  const request = Promise.resolve().then(() => quoteCart(JSON.parse(key))).finally(() => {
    if (pendingQuotes.get(key) === request) pendingQuotes.delete(key);
  });
  pendingQuotes.set(key, request);
  return request;
};
const sameDisplayCart = (previous, next) => previous && previous.customerId === next.customerId
  && previous.shippingMethodId === next.shippingMethodId
  && (previous.shoppingCartId === next.shoppingCartId || (!previous.shoppingCartId && Boolean(next.shoppingCartId)));

export const useCartAmounts = () => {
  const user = useSelector(state => state.aplicationConfig.userConfig);
  const key = JSON.stringify({
    lines: (user.cart?.ShoppingCartProducts || []).map(({productId, quantity, variantId, itemIds, note}) => ({productId, quantity, variantId, itemIds, note})),
    shoppingCartId: user.cart?.shoppingCartId,
    shippingMethodId: user.selectedShippingMethodId,
    customerId: user.client?.customerId,
    discountIds: (user.cart?.Discounts || user.cart?.discounts || []).map(discount => discount.discountId),
  });
  const [attempt, setAttempt] = useState(0);
  const [quote, setQuote] = useState({key:null, amounts:null, busy:true, error:false});
  const confirmed = useRef(null);
  useEffect(() => {
    const reader = {key, retry: () => setAttempt(value => value + 1)};
    retryReaders.add(reader);
    return () => retryReaders.delete(reader);
  }, [key]);
  useEffect(() => {
    let active = true;
    setQuote({key, amounts:null, busy:true, error:false});
    (async () => {
      try {
        const response = await readQuote(key);
        const amounts = response?.data;
        if (response?.error !== false || !["subtotal", "discounts", "base", "tax", "shipping", "total"].every(field => typeof amounts?.[field] === "number" && Number.isFinite(amounts[field]))) throw new Error("Amount unavailable");
        if (active) {
          confirmed.current = {payload: JSON.parse(key), amounts};
          setQuote({key, amounts, busy:false, error:false});
        }
      } catch {
        if (active) setQuote({key, amounts:null, busy:false, error:true});
      }
    })();
    return () => {active = false;};
  }, [key, attempt]);
  const current = quote.key === key ? quote : {amounts:null, busy:true, error:false};
  // Display-only history is never exposed as a payable current quote.
  const displayAmounts = current.amounts || (sameDisplayCart(confirmed.current?.payload, JSON.parse(key)) ? confirmed.current.amounts : null);
  return {...current, displayAmounts, retry:() => {
    if (!current.busy) retryReaders.forEach(reader => { if (reader.key === key) reader.retry(); });
  }};
};
